import IconGlobeShare from "@shared/components/icons/GlobeShare"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { InputBase } from "@shared/components/untitledui/base/input/input"
import { Label } from "@shared/components/untitledui/base/input/label"
import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@shared/components/untitledui/modal"
import { cx } from "@shared/components/untitledui/util/cx"
import { isValidUrl, withProtocol } from "@shared/lib/urls"
import type { Editor } from "@tiptap/core"
import { Node as PMNode } from "@tiptap/pm/model"
import { NodeSelection } from "@tiptap/pm/state"
import { Check } from "@untitledui/icons"
import { useRef, useState } from "react"
import { Radio as AriaRadio, RadioGroup as AriaRadioGroup } from "react-aria-components"
import { attachmentIdOfNode } from "../../analytics/node-ids"
import { reportMenuOpenChange } from "../../analytics/open-menu-event"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { asAttachmentId } from "../../analytics/types"
import { isLinkNode, type Link, type LinkDialogOrigin } from "../../extensions/link/link-attrs"
import { linkFromUrl } from "../../extensions/link/link-inline-extension"
import { getContainingCardId } from "../../util/card"

/**
 * The editor's core add/edit link dialog, opened by the toolbar's LinkButton, Mod-K, or the
 * link hover menu's Edit button. It operates on the unified link token
 * (docs/unified-links.md §6): editing the selected token in any form, or inserting a
 * new inline hyperlink token over the selection.
 *
 * The AddButton's "Add Link" button uses a different dialog, AddButtonLinkDialog,
 * which is visually similar but inserts links differently.
 */
export const EditorLinkDialog = ({
  editor,
  origin,
  onClose,
}: {
  editor: Editor
  /** The opener that started the dialog, or null while it is closed. */
  origin: LinkDialogOrigin | null
  onClose: () => void
}) => {
  const open = origin !== null
  const [url, setUrl] = useState("")
  const [title, setTitle] = useState("")
  const [style, setStyle] = useState<"hyperlink" | "preview">("hyperlink")
  const [mode, setMode] = useState<"Add" | "Edit">("Add")

  /** Edit mode: the selected token. Add mode: the selected text (possibly empty). */
  const initialSelection = useRef<{ pos: number; node: PMNode } | { text: string }>({ text: "" })

  const wasOpenRef = useRef(false)
  if (open !== wasOpenRef.current) {
    wasOpenRef.current = open
    // Reported here, not per-opener: all three openers funnel through this one state change, so
    // the closed → open edge fires exactly once whichever of them started it.
    reportMenuOpenChange(editor, "link-popover", open, () =>
      getContainingCardId(editor.state.doc, editor.state.selection.from),
    )
    if (open) {
      const selection = editor.state.selection
      if (selection instanceof NodeSelection && isLinkNode(selection.node)) {
        // Initialize from the selected token (any form).
        const attrs = selection.node.attrs as Link
        setTitle(attrs.title || "")
        setUrl(attrs.url)
        setMode("Edit")
        initialSelection.current = { pos: selection.from, node: selection.node }
      } else {
        // Initialize from the selected text; submitting inserts a new token over it.
        const selectedText = editor.state.doc.textBetween(selection.from, selection.to)
        setTitle(selectedText)
        setUrl("")
        setMode("Add")
        initialSelection.current = { text: selectedText }
      }
    }
  }

  const handleSubmit = (surface: LinkDialogOrigin) => {
    const userUrl = url.trim()
    const processedUrl = withProtocol(userUrl)
    const userTitle = title.trim() || null

    const chain = editor.chain()
    const initial = initialSelection.current
    if ("node" in initial) {
      const { pos, node } = initial
      // A token with no attachment identity has nothing for an attachment event to name, so
      // the mutation below runs unlabeled rather than reporting an unidentified attachment.
      const attachmentId = attachmentIdOfNode(node)
      if (userUrl === "") {
        if (attachmentId) {
          chain.label(OPERATION_CONTRACTS.OP_REMOVE_LINK, surface, { attachmentId })
        }
        // Erasing the URL unlinks: an inline token falls back to its plain label text;
        // a block token (an attachment line) is just removed.
        chain.command(({ tr }) => {
          const label = userTitle ?? ((node.attrs as Link).title || null)
          if (node.type.name === "linkInline" && label) {
            tr.replaceWith(pos, pos + node.nodeSize, editor.schema.text(label))
          } else {
            tr.delete(pos, pos + node.nodeSize)
          }
          return true
        })
      } else {
        // Update the token in place — same node, same form, same attachment identity.
        // A changed URL invalidates the cached preview metadata (it described the old
        // destination); a title-only edit keeps it.
        const urlChanged = processedUrl !== (node.attrs as Link).url
        // Compared, not assumed: a Done with nothing edited still runs `setNodeMarkup`, and a
        // label that named a rename there would be claiming an edit the user never made.
        const titleChanged = userTitle !== ((node.attrs as Link).title || null)
        if (attachmentId) {
          if (urlChanged) {
            chain.label(OPERATION_CONTRACTS.OP_EDIT_LINK, surface, {
              attachmentId,
              url: processedUrl,
            })
          } else if (titleChanged) {
            chain.label(OPERATION_CONTRACTS.OP_RENAME_LINK, surface, {
              attachmentId,
              title: userTitle,
            })
          }
        }
        chain.command(({ tr }) => {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            url: processedUrl,
            title: userTitle,
            ...(urlChanged && {
              previewTitle: null,
              previewDescription: null,
              previewImageUrl: null,
              previewFaviconUrl: null,
            }),
          })
          return true
        })
      }
    } else if (userUrl !== "") {
      // Insert a new inline hyperlink token over the selection (§6: dialog path —
      // source "link"), labeled by the user's title, the selected text, or the URL.
      const link = linkFromUrl(processedUrl, userTitle || initial.text || processedUrl)
      const token = editor.schema.nodes.linkInline.create(link)
      chain.label(OPERATION_CONTRACTS.OP_INSERT_LINK, surface, {
        attachmentId: asAttachmentId(link.attachmentId),
        url: processedUrl,
      })
      chain.command(({ tr }) => {
        tr.replaceSelectionWith(token, false)
        return true
      })
    }
    chain.focus().run()

    onClose()
  }

  // An empty URL unlinks, which only Edit mode can do.
  const trimmedUrl = url.trim()
  const isValid = trimmedUrl === "" ? mode === "Edit" : isValidUrl(trimmedUrl)

  return (
    <LinkDialogCore
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      mode={mode}
      submitLabel="Done"
      url={url}
      setUrl={setUrl}
      title={title}
      setTitle={setTitle}
      style={style}
      setStyle={setStyle}
      isValid={isValid}
      onClose={onClose}
      onSubmit={() => {
        if (origin) handleSubmit(origin)
      }}
    />
  )
}

/**
 * Shared display for the link add/edit dialogs (EditorLinkDialog and AddButtonLinkDialog),
 * which differ only in how they insert links.
 */
export const LinkDialogCore = ({
  open,
  onOpenChange,
  mode,
  submitLabel,
  url,
  setUrl,
  title,
  setTitle,
  style,
  setStyle,
  isValid,
  onClose,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: "Add" | "Edit"
  submitLabel: string
  url: string
  setUrl: (url: string) => void
  title: string
  setTitle: (title: string) => void
  style: "hyperlink" | "preview"
  setStyle: (style: "hyperlink" | "preview") => void
  isValid: boolean
  onClose: () => void
  onSubmit: () => void
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid) {
      e.preventDefault()
      onSubmit()
    }
  }

  return (
    // The wash is the designs' teal in the brand's sky (sky-100), spelled out because an
    // `@theme` var only ships when a utility of its own uses it.
    <ModalContent
      isOpen={open}
      onOpenChange={onOpenChange}
      className="bg-[linear-gradient(-5.79deg,#ffffff_50.9%,#e0f2fe_86.39%)] sm:max-w-100"
    >
      {/* Header, body and footer each pin their own p-6: the modal's own --gutter widens to 32px
          at sm, which would inset the title and the buttons from the fields. */}
      <ModalHeader className="flex shrink-0 flex-col items-start gap-4 space-y-0 p-6 pb-5">
        <IconGlobeShare className="size-14.25" />
        {/* The sm: repeat is load-bearing: ModalTitle shrinks itself to text-base at sm. */}
        <ModalTitle className="text-[26px]/6 font-black tracking-[-0.26px] sm:text-[26px]/6">
          {mode} Link
        </ModalTitle>
      </ModalHeader>
      {/* ModalBody, not a bare div: ModalFooter zeroes its own top padding when no
          data-slot="dialog-body" precedes it, which drops the buttons onto the last field. */}
      <ModalBody className="grid min-h-0 gap-5 overflow-y-auto p-6 py-0">
        <div className="grid gap-1.5">
          {/* mb-0: the app ships a global `label { margin-bottom: 5px }` (bootstrap
              forms.css), which pushes the field off its label and breaks the pairing. */}
          <Label htmlFor="link-url" className="mb-0 font-semibold">
            URL
          </Label>
          <InputBase
            id="link-url"
            type="url"
            autoFocus={true}
            placeholder="https://...."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            inputClassName="text-lg/6"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="link-title" className="mb-0 font-semibold">
            Text to show instead of the link (Optional)
          </Label>
          <InputBase
            id="link-title"
            type="text"
            placeholder="Link Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            inputClassName="text-lg/6"
          />
        </div>
        {/* The preview-vs-hyperlink choice is hidden for now; we'll enable it later. */}
        {/* <LinkStyleRadioGroup value={style} onChange={setStyle} /> */}
      </ModalBody>
      <ModalFooter className="shrink-0 flex-row p-6 pt-8">
        <Button color="secondary" size="md" className="flex-1" onPress={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          size="md"
          className="flex-1"
          onPress={onSubmit}
          isDisabled={!isValid}
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </ModalContent>
  )
}

type LinkStyle = "hyperlink" | "preview"

const LINK_STYLE_OPTIONS: { value: LinkStyle; title: string; description: string; edge: string }[] =
  [
    {
      value: "hyperlink",
      title: "Normal Link",
      description: "A normal link",
      edge: "rounded-l-xl",
    },
    {
      value: "preview",
      title: "Preview",
      description: "A preview of the link",
      edge: "rounded-r-xl border-l-0",
    },
  ]

/**
 * Segmented two-card radio for choosing how a link is inserted, per the "New Menus" design.
 */
const LinkStyleRadioGroup = ({
  value,
  onChange,
}: {
  value: LinkStyle
  onChange: (value: LinkStyle) => void
}) => (
  <AriaRadioGroup
    aria-label="Link style"
    value={value}
    onChange={(v) => onChange(v as LinkStyle)}
    className="flex w-full items-stretch"
  >
    {LINK_STYLE_OPTIONS.map((option) => (
      <AriaRadio
        key={option.value}
        value={option.value}
        className={({ isSelected, isFocusVisible }) =>
          cx(
            "flex flex-1 cursor-pointer items-start gap-4 border border-secondary bg-primary p-4",
            option.edge,
            isSelected && "z-10 border-l border-brand",
            isFocusVisible && "outline-2 outline-offset-2 outline-focus-ring",
          )
        }
      >
        {({ isSelected }) => (
          <>
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <div className="flex items-start gap-1">
                <p className="text-sm font-semibold whitespace-nowrap text-secondary">
                  {option.title}
                </p>
              </div>
              <p className="w-full text-sm font-normal text-tertiary">{option.description}</p>
            </div>
            <div
              className={cx(
                "flex size-4 shrink-0 items-center justify-center rounded ring-1 ring-inset",
                isSelected ? "bg-brand-solid ring-brand-solid" : "bg-primary ring-primary",
              )}
            >
              {isSelected && <Check className="size-3 text-white" />}
            </div>
          </>
        )}
      </AriaRadio>
    ))}
  </AriaRadioGroup>
)
